import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { selectAdminToken } from "../../admin/sessionSlice";
import "./stage3.css";
import { fireGameAlert } from "./gameAlert";
import {
  buildWordSearchGrid,
  cellKey,
  getCellsOnPath,
} from "./wordSearchUtils";
import {
  STAGE_ROW_KEY,
  clearStageActive,
  getRedirectForPlayedStage,
  hydrateCompletedStagesFromRecord,
  markStageCompleted,
  normalizePlayerName,
  saveGameStageAndReport,
  setStageActive,
} from "../../functions/stageReportSync";

const STAGE_NUM = 3;
const GRID_SIZE = 15;
/** old/js/script.js: points >= 5 reveals the Submit button. */
const MIN_WORDS_TO_UNLOCK_SUBMIT = 5;
const POINTS_PER_WORD = 1;

const STAGE3_BG = `${process.env.PUBLIC_URL}/assets/game/stage3-bg.jpg`;
const STAGE3_BG_MOBILE = `${process.env.PUBLIC_URL}/assets/game/stage3_mob.png`;
const POPUP_BG = `${process.env.PUBLIC_URL}/assets/game/popup-bg.png`;

/** The DB only stores stage3's point total, not which words earned it — persist
 * the actual found words here so a same-tab reload can restore the boxes/highlights
 * instead of just the score (the grid itself is re-randomized on every mount). */
const FOUND_WORDS_STORAGE_KEY = "stage3FoundWords";

function readStoredFoundWords() {
  try {
    const raw = JSON.parse(sessionStorage.getItem(FOUND_WORDS_STORAGE_KEY) || "[]");
    return Array.isArray(raw) ? raw.filter((w) => WORDS.includes(w)) : [];
  } catch {
    return [];
  }
}

const MOBILE_PORTRAIT_MQ = "(max-width: 768px) and (orientation: portrait)";

const WORDS = [
  "COLLABORATION",
  "TRUST",
  "INTEGRITY",
  "EMPATHY",
  "COMMITMENT",
  "LOYALTY",
  "SYNERGY",
  "RESPECT",
];

const RULES_TEXT =
  "The final 3 days are dedicated to Goddess Saraswati, the embodiment of knowledge and wisdom. " +
  "These days emphasize the importance of acquiring knowledge and using it for the greater good, " +
  "ensuring that wisdom prevails over ignorance";

function parseClockToSeconds(clock) {
  const parts = String(clock ?? "00:00")
    .trim()
    .split(":")
    .map((p) => parseInt(p, 10));
  if (parts.some((n) => !Number.isFinite(n))) return 0;
  if (parts.length >= 3) return parts[0] * 3600 + parts[1] * 60 + parts[2];
  if (parts.length >= 2) return parts[0] * 60 + parts[1];
  return parts[0] || 0;
}

function formatSecondsToClock(totalSec) {
  const sec = Math.max(0, Math.floor(totalSec));
  const min = Math.floor(sec / 60);
  const rem = sec % 60;
  return `${String(min).padStart(2, "0")}:${String(rem).padStart(2, "0")}`;
}

function getStoredUser() {
  try {
    return JSON.parse(sessionStorage.getItem("userData") || "{}");
  } catch {
    return {};
  }
}

export default function Stage3() {
  const navigate = useNavigate();
  const adminToken = useSelector(selectAdminToken);

  const [stageRowId, setStageRowId] = useState(
    () => sessionStorage.getItem(STAGE_ROW_KEY) || null
  );
  const [dbReady, setDbReady] = useState(false);
  const [elapsedSec, setElapsedSec] = useState(0);
  const [stage3Points, setStage3Points] = useState(0);
  const [displayTotalScore, setDisplayTotalScore] = useState(0);
  const [foundWords, setFoundWords] = useState(() => new Set(readStoredFoundWords()));
  /** "row,col" keys of every cell belonging to an already-found word — stays highlighted. */
  const [foundCellKeys, setFoundCellKeys] = useState(() => new Set());
  const [activeCells, setActiveCells] = useState([]);
  const [showRules, setShowRules] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const gridRef = useRef(null);
  const gridWrapRef = useRef(null);
  const isDraggingRef = useRef(false);
  const startCellRef = useRef(null);
  const activeCellsRef = useRef([]);
  const foundRef = useRef(new Set());
  const elapsedRef = useRef(0);
  const baseElapsedRef = useRef(0);
  const sessionStartRef = useRef(null);
  const otherStagesTotalRef = useRef(0);
  const baselinePointsRef = useRef(0);
  const submittedRef = useRef(false);
  const timerStartedRef = useRef(false);
  const demoRedirectTriggeredRef = useRef(false);

  const storedUser = useMemo(() => getStoredUser(), []);
  const isDemoBypass = useMemo(() => {
    const role = String(storedUser.role || "").toLowerCase();
    const source = String(storedUser.source || "").toUpperCase();
    const sessionId = String(storedUser.sessionId || "");
    return (
      sessionId === "demobypass" ||
      role === "demobypass" ||
      source === "DEMO" ||
      sessionId.startsWith("demo")
    );
  }, [storedUser]);
  const backendBase = String(process.env.REACT_APP_BACKEND_URL || "").replace(
    /\/+$/,
    ""
  );

  const puzzle = useMemo(() => buildWordSearchGrid(WORDS, GRID_SIZE), []);
  const targetWords = useMemo(() => new Set(WORDS), []);

  // Persist found words so a same-tab reload can restore the boxes below.
  useEffect(() => {
    try {
      sessionStorage.setItem(
        FOUND_WORDS_STORAGE_KEY,
        JSON.stringify([...foundWords])
      );
    } catch {
      /* ignore */
    }
  }, [foundWords]);

  // Runs once on mount: for any words restored from sessionStorage, look up
  // where buildWordSearchGrid actually placed them on *this* freshly-generated
  // grid and highlight those cells — the grid layout itself isn't persisted,
  // so a literal cell-position restore isn't possible, only a re-derived one.
  useEffect(() => {
    if (!foundWords.size) return;
    const restored = new Set();
    foundWords.forEach((word) => {
      const placement = puzzle.placements.find((p) => p.word === word);
      if (!placement) return;
      const { row, col, direction } = placement;
      const len = word.length;
      const end =
        direction === 0
          ? { row, col: col + len - 1 }
          : direction === 1
            ? { row: row + len - 1, col }
            : { row: row + len - 1, col: col + len - 1 };
      getCellsOnPath({ row, col }, end).forEach((c) =>
        restored.add(cellKey(c.row, c.col))
      );
    });
    if (restored.size) {
      setFoundCellKeys((prev) => new Set([...prev, ...restored]));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [puzzle]);

  useEffect(() => {
    const html = document.documentElement;
    const body = document.body;
    const prev = {
      backgroundImage: body.style.backgroundImage,
      backgroundSize: body.style.backgroundSize,
      backgroundRepeat: body.style.backgroundRepeat,
      backgroundPosition: body.style.backgroundPosition,
      backgroundAttachment: body.style.backgroundAttachment,
    };
    const applyBackground = () => {
      const isMobilePortrait = window.matchMedia(MOBILE_PORTRAIT_MQ).matches;
      body.style.backgroundImage = `url("${isMobilePortrait ? STAGE3_BG_MOBILE : STAGE3_BG}")`;
      body.style.backgroundSize = isMobilePortrait ? "cover" : "100% 100%";
      body.style.backgroundRepeat = "no-repeat";
      body.style.backgroundPosition = "center center";
      body.style.backgroundAttachment = "fixed";
    };

    html.classList.add("stage3-lock");
    body.classList.remove("common-bg");
    body.classList.add("stage3-wordsearch-page");
    applyBackground();
    window.addEventListener("resize", applyBackground);
    window.addEventListener("orientationchange", applyBackground);
    return () => {
      html.classList.remove("stage3-lock");
      body.classList.remove("stage3-wordsearch-page");
      window.removeEventListener("resize", applyBackground);
      window.removeEventListener("orientationchange", applyBackground);
      body.style.backgroundImage = prev.backgroundImage;
      body.style.backgroundSize = prev.backgroundSize;
      body.style.backgroundRepeat = prev.backgroundRepeat;
      body.style.backgroundPosition = prev.backgroundPosition;
      body.style.backgroundAttachment = prev.backgroundAttachment;
    };
  }, []);

  useEffect(() => {
    foundRef.current = foundWords;
  }, [foundWords]);

  useEffect(() => {
    if (!dbReady || timerStartedRef.current) return;

    timerStartedRef.current = true;
    sessionStartRef.current = Date.now();

    const tick = setInterval(() => {
      const sessionSec = Math.floor(
        (Date.now() - sessionStartRef.current) / 1000
      );
      const elapsed = baseElapsedRef.current + sessionSec;
      elapsedRef.current = elapsed;
      setElapsedSec(elapsed);
    }, 1000);

    return () => clearInterval(tick);
  }, [dbReady]);

  useEffect(() => {
    setDisplayTotalScore(otherStagesTotalRef.current + stage3Points);
  }, [stage3Points]);

  useEffect(() => {
    let cancelled = false;

    async function loadStageFromDb() {
      const uid = storedUser.userId || storedUser.userid || storedUser.id;
      const sessionId = storedUser.sessionId;
      const organizationId = storedUser.organizationId;

      if (isDemoBypass || !backendBase || !adminToken) {
        if (!cancelled) setDbReady(true);
        return;
      }

      if (!uid || !sessionId || !organizationId) {
        if (!cancelled) setDbReady(true);
        return;
      }

      try {
        let rowId = sessionStorage.getItem(STAGE_ROW_KEY);

        if (!rowId) {
          const startRes = await fetch(`${backendBase}/welcomeStageStart`, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${adminToken}`,
            },
            body: JSON.stringify({
              userId: uid,
              sessionId,
              organizationId,
              email: storedUser.email || "",
              emp_id: storedUser.employeeId || storedUser.emp_id || "",
              name: normalizePlayerName(storedUser),
              user_type: storedUser.user_type || storedUser.userType || "",
            }),
          });
          const startData = await startRes.json().catch(() => ({}));
          if (startRes.ok && startData.success && startData.id != null) {
            rowId = String(startData.id);
            sessionStorage.setItem(STAGE_ROW_KEY, rowId);
          }
        }

        if (!rowId) {
          if (!cancelled) setDbReady(true);
          return;
        }

        if (!cancelled) setStageRowId(rowId);

        const stageRes = await fetch(`${backendBase}/getGameStage`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${adminToken}`,
          },
          body: JSON.stringify({
            userId: uid,
            sessionId,
            organizationId,
            stageId: rowId,
          }),
        });
        const stageData = await stageRes.json().catch(() => ({}));

        if (cancelled) return;

        if (stageRes.ok && stageData.success && stageData.stage) {
          const st = stageData.stage;
          hydrateCompletedStagesFromRecord(st);
          const resumeRoute = getRedirectForPlayedStage(st, STAGE_NUM);
          if (resumeRoute) {
            navigate(resumeRoute, { replace: true });
            return;
          }

          const dbTimeSec = parseClockToSeconds(st.time);
          const dbTotal = Number(st.total_score) || 0;
          const dbStage3 = Number(st.stage3) || 0;

          baseElapsedRef.current = dbTimeSec;
          elapsedRef.current = dbTimeSec;
          setElapsedSec(dbTimeSec);

          otherStagesTotalRef.current = Math.max(0, dbTotal - dbStage3);
          baselinePointsRef.current = dbStage3;
          setStage3Points(dbStage3);
          setDisplayTotalScore(dbTotal);
        }

        setStageActive(STAGE_NUM, storedUser);
        setDbReady(true);
      } catch (err) {
        console.error("Stage3 loadStageFromDb:", err);
        if (!cancelled) setDbReady(true);
      }
    }

    loadStageFromDb();
    return () => {
      cancelled = true;
    };
  }, [backendBase, adminToken, isDemoBypass, storedUser, navigate]);

  const finishStage = useCallback(
    async (skip = false) => {
      if (submitting || submittedRef.current) return;
      submittedRef.current = true;
      setSubmitting(true);

      // old/stage3.php's send_skip() submits `initalPoints` (points already saved
      // before this attempt); send() (manual Submit) submits whatever was found.
      const stage3Score = skip ? baselinePointsRef.current : stage3Points;
      const timeStr = formatSecondsToClock(elapsedRef.current);
      const stageId = stageRowId || sessionStorage.getItem(STAGE_ROW_KEY);
      const finalTotal = otherStagesTotalRef.current + stage3Score;

      if (!isDemoBypass && backendBase && adminToken && stageId) {
        const saveResult = await saveGameStageAndReport({
          backendBase,
          adminToken,
          storedUser,
          isDemoBypass,
          stageId,
          stageNumber: STAGE_NUM,
          score: stage3Score,
          time: timeStr,
          total_score: finalTotal,
        });
        if (saveResult.total_score != null) {
          setDisplayTotalScore(saveResult.total_score);
        }
      }

      clearStageActive(STAGE_NUM);
      markStageCompleted(STAGE_NUM, storedUser);
      try {
        sessionStorage.removeItem(FOUND_WORDS_STORAGE_KEY);
      } catch {
        /* ignore */
      }

      if (isDemoBypass) {
        window.location.href =
          String(process.env.REACT_APP_BASE_URL || "").trim() || "/";
        return;
      }
      await new Promise((resolve) => setTimeout(resolve, 1200));
      navigate("/stage4");
    },
    [submitting, stage3Points, storedUser, isDemoBypass, backendBase, adminToken, stageRowId, navigate]
  );

  const handleSkip = useCallback(() => {
    finishStage(true);
  }, [finishStage]);

  const markWordFound = useCallback(
    (word, cells) => {
      if (foundRef.current.has(word)) return;
      const isFirstWordCompletion = foundRef.current.size === 0;

      setFoundWords((prev) => {
        const next = new Set(prev);
        next.add(word);
        return next;
      });
      foundRef.current.add(word);
      setFoundCellKeys((prev) => {
        const next = new Set(prev);
        cells.forEach(({ row, col }) => next.add(cellKey(row, col)));
        return next;
      });
      setStage3Points((p) => p + POINTS_PER_WORD);

      if (isDemoBypass && isFirstWordCompletion && !demoRedirectTriggeredRef.current) {
        demoRedirectTriggeredRef.current = true;
        setTimeout(() => {
          void fireGameAlert({
            icon: "info",
            text: "Thank you for playing. Subscribe to any PLAN to play with your peers.",
            confirmButtonText: "OK",
            allowOutsideClick: false,
            allowEscapeKey: false,
          }).then(() => {
            window.location.assign("https://extramileplay.com/plans");
          });
        }, 400);
      }
    },
    [isDemoBypass]
  );

  const resolveSelection = useCallback(
    (cells) => {
      if (!cells.length) return;
      const selected = cells
        .map(({ row, col }) => puzzle.grid[row]?.[col] || "")
        .join("");
      const reversed = [...selected].reverse().join("");

      let matched = null;
      if (targetWords.has(selected)) matched = selected;
      else if (targetWords.has(reversed)) matched = reversed;

      if (matched && !foundRef.current.has(matched)) {
        markWordFound(matched, cells);
      }
    },
    [puzzle, targetWords, markWordFound]
  );

  const updateActivePath = useCallback((endRow, endCol) => {
    const start = startCellRef.current;
    if (!start) return;
    const cells = getCellsOnPath(start, { row: endRow, col: endCol });
    activeCellsRef.current = cells;
    setActiveCells(cells);
  }, []);

  const onPointerDown = useCallback((row, col) => {
    isDraggingRef.current = true;
    startCellRef.current = { row, col };
    const cells = [{ row, col }];
    activeCellsRef.current = cells;
    setActiveCells(cells);
  }, []);

  const onPointerEnter = useCallback(
    (row, col) => {
      if (!isDraggingRef.current) return;
      updateActivePath(row, col);
    },
    [updateActivePath]
  );

  const onPointerUp = useCallback(() => {
    if (!isDraggingRef.current) return;
    isDraggingRef.current = false;
    resolveSelection(activeCellsRef.current);
    activeCellsRef.current = [];
    setActiveCells([]);
    startCellRef.current = null;
  }, [resolveSelection]);

  const onGridTouchMove = useCallback(
    (e) => {
      if (!isDraggingRef.current) return;
      e.preventDefault();
      const touch = e.touches[0];
      if (!touch) return;
      const el = document.elementFromPoint(touch.clientX, touch.clientY);
      const cell = el?.closest?.("[data-row][data-col]");
      if (cell?.dataset?.row != null) {
        onPointerEnter(Number(cell.dataset.row), Number(cell.dataset.col));
      }
    },
    [onPointerEnter]
  );

  useEffect(() => {
    const up = () => onPointerUp();
    window.addEventListener("mouseup", up);
    window.addEventListener("touchend", up);
    return () => {
      window.removeEventListener("mouseup", up);
      window.removeEventListener("touchend", up);
    };
  }, [onPointerUp]);

  const fitGrid = useCallback(() => {
    const wrap = gridWrapRef.current;
    if (!wrap) return;
    const size = Math.max(
      0,
      Math.floor(Math.min(wrap.clientWidth, wrap.clientHeight, 560))
    );
    if (size <= 0) return;
    const next = `${size}px`;
    if (wrap.style.getPropertyValue("--puzzle-size") !== next) {
      wrap.style.setProperty("--puzzle-size", next);
    }
  }, []);

  useLayoutEffect(() => {
    fitGrid();
  }, [fitGrid]);

  useEffect(() => {
    const wrap = gridWrapRef.current;
    if (!wrap) return undefined;
    const observer = new ResizeObserver(fitGrid);
    observer.observe(wrap);
    window.addEventListener("orientationchange", fitGrid);
    window.addEventListener("resize", fitGrid);
    return () => {
      observer.disconnect();
      window.removeEventListener("orientationchange", fitGrid);
      window.removeEventListener("resize", fitGrid);
    };
  }, [fitGrid]);

  if (!dbReady) {
    return <div className="stage3-loading">Loading game…</div>;
  }

  const activeSet = new Set(activeCells.map((c) => cellKey(c.row, c.col)));
  const clock = formatSecondsToClock(elapsedSec);
  const canSubmit = foundWords.size >= MIN_WORDS_TO_UNLOCK_SUBMIT;

  return (
    <div className="stage3-page">
      <div className="stage3-hud-row">
        <div className="stage3-hud-col stage3-hud-col--left">
          <div className="stage3-timericon" aria-label={`Time ${clock}`}>
            Time: {clock}
          </div>
        </div>
        <div className="stage3-hud-col stage3-hud-col--center">
          <div className="stage3-heading-box">
            <h1 className="stage3-head-title">
              Discover 8 key drivers for achieving the greater good for ourselves, our
              team and organization at large
            </h1>
          </div>
        </div>
        <div className="stage3-hud-col stage3-hud-col--right">
          <div className="stage3-point-box">
            <h4>Score:</h4> <span>{displayTotalScore}</span>
          </div>
          <button
            type="button"
            className="stage3-timericon stage3-skip"
            disabled={submitting}
            onClick={handleSkip}
          >
            Skip
          </button>
        </div>
      </div>

      <div className="stage3-body">
        <div className="stage3-grid-wrap" ref={gridWrapRef}>
          <div
            ref={gridRef}
            className="stage3-grid"
            style={{
              gridTemplateColumns: `repeat(${GRID_SIZE}, minmax(0, 1fr))`,
              gridTemplateRows: `repeat(${GRID_SIZE}, minmax(0, 1fr))`,
            }}
            onMouseLeave={() => {
              if (isDraggingRef.current) onPointerUp();
            }}
            onTouchMove={onGridTouchMove}
          >
            {puzzle.grid.map((row, r) =>
              row.map((letter, c) => {
                const key = cellKey(r, c);
                const isActive = activeSet.has(key);
                const isFound = foundCellKeys.has(key);
                return (
                  <div
                    key={key}
                    className={`stage3-cell${isFound ? " stage3-cell--found" : ""}${
                      isActive ? " stage3-cell--active" : ""
                    }`}
                    onMouseDown={(e) => {
                      e.preventDefault();
                      onPointerDown(r, c);
                    }}
                    onMouseEnter={() => onPointerEnter(r, c)}
                    onTouchStart={(e) => {
                      e.preventDefault();
                      onPointerDown(r, c);
                    }}
                    data-row={r}
                    data-col={c}
                  >
                    {letter}
                  </div>
                );
              })
            )}
          </div>
        </div>

        <div className="stage3-found-words">
          {[...foundWords].map((word) => (
            <input
              key={word}
              type="text"
              readOnly
              className="stage3-found-word"
              value={word}
            />
          ))}
        </div>
      </div>

      {canSubmit ? (
        <div className="stage3-submit-wrap">
          <button
            type="button"
            className="stage3-btn-submit"
            disabled={submitting}
            onClick={() => finishStage(false)}
          >
            Submit
          </button>
        </div>
      ) : null}

      {showRules ? (
        <div className="stage3-rules-overlay" role="dialog" aria-modal="true">
          <div
            className="stage3-rules-card"
            style={{
              backgroundImage: `linear-gradient(rgba(0,0,0,.35), rgba(0,0,0,.35)), url("${POPUP_BG}")`,
            }}
          >
            <button
              type="button"
              className="stage3-rules-close"
              aria-label="Close"
              onClick={() => setShowRules(false)}
            >
              &times;
            </button>
            <p>{RULES_TEXT}</p>
          </div>
        </div>
      ) : null}
    </div>
  );
}
